import axios from 'axios';

// Use relative URLs in production so Vercel serves the matching /api routes.
const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined
  ? process.env.REACT_APP_API_URL
  : process.env.NODE_ENV === 'development'
    ? 'http://localhost:5001'
    : '';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true
});

function notifyIfStaleCache(response, cacheHeader, queryKey) {
  if (typeof window === 'undefined') return;
  if (response.headers?.[cacheHeader] !== 'stale') return;

  window.dispatchEvent(new CustomEvent('calendar-app-cache-stale', {
    detail: { queryKey },
  }));
}

// Auth
export const login = async (username, password) => {
  const response = await api.post('/api/auth/login', { username, password });
  return response.data;
};

export const getAuthUrl = async () => {
  const response = await api.get('/api/auth/google');
  return response.data.url;
};

export const checkAuthStatus = async () => {
  try {
    const response = await api.get('/api/auth/status');
    return response.data;
  } catch (error) {
    return { authenticated: false };
  }
};

export const logout = async () => {
  await api.post('/api/auth/logout');
};

export const disconnectGoogle = async () => {
  await api.post('/api/auth/google/disconnect');
};

// Calendars
export const getCalendars = async () => {
  const response = await api.get('/api/calendars');
  notifyIfStaleCache(response, 'x-calendars-cache', ['calendars']);
  return response.data;
};

// Events
export const getEvents = async (timeMin, timeMax) => {
  const response = await api.get('/api/events', {
    params: { timeMin, timeMax }
  });
  notifyIfStaleCache(response, 'x-calendar-cache', ['events']);
  return response.data;
};

export const getEvent = async (eventId, calendarId = 'primary') => {
  const response = await api.get(`/api/events/${eventId}`, {
    params: { calendarId }
  });
  return response.data;
};

export const createEvent = async (event) => {
  const response = await api.post('/api/events', event);
  return response.data;
};

export const updateEvent = async (eventId, event) => {
  const response = await api.put(`/api/events/${eventId}`, event);
  return response.data;
};

export const deleteEvent = async (eventId, calendarId = 'primary') => {
  const response = await api.delete(`/api/events/${eventId}`, {
    params: { calendarId }
  });
  return response.data;
};

// Tasks
export const getTasks = async () => {
  const response = await api.get('/api/tasks');
  notifyIfStaleCache(response, 'x-tasks-cache', ['tasks']);
  return response.data;
};

export const createTask = async (taskListId, task) => {
  const response = await api.post('/api/tasks', { taskListId, task });
  return response.data;
};

export const updateTask = async (taskListId, taskId, task) => {
  const response = await api.put(`/api/tasks/${taskId}`, { taskListId, task });
  return response.data;
};

export const deleteTask = async (taskListId, taskId) => {
  const response = await api.delete(`/api/tasks/${taskId}`, {
    params: { taskListId }
  });
  return response.data;
};

// Notion Notes
export const getNotionNotes = async () => {
  const response = await api.get('/api/notion-notes');
  return response.data;
};

export const getNotionNoteContent = async (id) => {
  const response = await api.get('/api/notion-notes', { params: { id } });
  return response.data;
};

export const createNotionNote = async (note) => {
  const response = await api.post('/api/notion-notes', note);
  return response.data;
};

export const updateNotionNote = async (note) => {
  const response = await api.put('/api/notion-notes', note);
  return response.data;
};

export const deleteNotionNote = async (id) => {
  const response = await api.delete('/api/notion-notes', { params: { id } });
  return response.data;
};

// Published Notes
export const publishNote = async (noteId) => {
  const response = await api.post('/api/published-notes', { noteId });
  return response.data;
};

export const unpublishNote = async (noteId) => {
  const response = await api.delete('/api/published-notes', { params: { noteId } });
  return response.data;
};

export const getPublishedNote = async (slug) => {
  const response = await api.get('/api/published-notes', { params: { slug } });
  return response.data;
};

// Settings
export const getSettings = async () => {
  const response = await api.get('/api/settings');
  return response.data;
};

export const updateSettings = async (settings) => {
  const response = await api.put('/api/settings', settings);
  return response.data;
};

// Image upload
export const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/api/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.url;
};

export default api;
